import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Popover, Progress } from 'antd';
import { getTheme } from 'Store/MultiDomain/MultiDomain.selector';
import { Icon } from 'Components/UI';
import { UploadStatus } from '../../extensions/CustomImage';
import {
  ImageContainer,
  ProgressOverlay,
  ButtonOverlay,
  Container,
  Card,
  CardItem,
} from './styles';

interface IImageComponentRender {
  id?: string;
  src?: string;
  alt?: string;
  uploadStatus?: UploadStatus;
  progress?: number;
  isDynamic?: boolean;
  onRemove?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onOpen?: () => void;
}

const ImageComponentRender: FC<IImageComponentRender> = ({
  src,
  alt,
  uploadStatus = 'completed',
  progress = 100,
  isDynamic,
  onRemove,
  onDelete,
  onDownload,
  onOpen,
}) => {
  const { t } = useTranslation('activities');
  const theme = useSelector(getTheme);
  const [open, setOpen] = useState(false);
  const isUploading = uploadStatus === 'uploading' || uploadStatus === 'pending';
  const hasError = uploadStatus === 'error';

  const hide = () => {
    setOpen(false);
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  }

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
    }
    hide();
  }

  const handleOpen = () => {
    if (onOpen) {
      onOpen();
    }
    hide();
  }

  const overlay = (
    <Container>
      <Card>
        <CardItem onClick={handleDownload}>
          <Icon icon="link" className="cardItemIcon" />
          <span className="cardItemText">
            Fazer download
          </span>
        </CardItem>
      </Card>
      <div className="dividerContainer" ><div className="divider" /></div>
      <Card>
        <CardItem onClick={handleOpen}>
          <Icon icon="excluir" className="cardItemIcon" />
          <span className="cardItemText">
            Abrir em uma nova guia
          </span>
        </CardItem>
      </Card>
    </Container>
  )

  return (
    <ImageContainer isDynamic={isDynamic}>
      <img
        src={src}
        alt={alt || t('comments.attachedImageAlt')}
      />

      <div className="imageOverlay" />

      {isDynamic && isUploading && (
        <ProgressOverlay>
          <Progress
            percent={progress}
            size={[98, 5]}
            showInfo={false}
            trailColor={theme.colors.surface.surface}
            strokeColor={theme.colors.primary.primary}
            className="progressBar"
          />
        </ProgressOverlay>
      )}

      {isDynamic && (isUploading || hasError) && onRemove && (
        <ButtonOverlay
          className="btnOverlay"
          onClick={onRemove}
        >
          <Icon icon="excluir" customSize={12} className="iconBtnOverlay" />
        </ButtonOverlay>
      )}

      {isDynamic && !isUploading && !hasError && onDelete && (
        <ButtonOverlay
          className="btnOverlay"
          onClick={onDelete}
        >
          <Icon icon="excluir" customSize={12} className="iconBtnOverlay" />
        </ButtonOverlay>
      )}

      {!isDynamic && (
        <Popover
          content={overlay}
          placement="bottomRight"
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          overlayInnerStyle={{ padding: 0 }}
        >
          <ButtonOverlay className="btnOverlay">
            <Icon icon="moreActions" customSize={12} className="iconBtnOverlay" />
          </ButtonOverlay>
        </Popover>
      )}
    </ImageContainer>
  );
};

export default ImageComponentRender;
